import { useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const FORM_SUBMIT_URL = "https://readdy.ai/api/form/db8tsdrk13ra8mig8us0";
const MESSAGE_LIMIT = 500;

const contactInfo = [
  { icon: "ri-mail-line", label: "邮箱", value: "hello@hanime.example.com" },
  { icon: "ri-weibo-fill", label: "微博", value: "@h动漫官方" },
  { icon: "ri-wechat-fill", label: "微信公众号", value: "h动漫情报站" },
  { icon: "ri-time-line", label: "办公时间", value: "周一至周五 10:00 - 19:00" },
];

export default function ContactPage() {
  useDocumentMeta(
    "联系我们 - h动漫",
    "联系h动漫编辑团队，提交内容建议、合作洽谈或纠错反馈，我们会尽快回复。"
  );

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      setMessage("");
      return;
    }
    payload.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
        setMessage("");
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <div className="max-w-4xl mx-auto">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />

        <header className="mt-4">
          <h1 className="font-heading font-extrabold text-foreground-950 text-3xl md:text-4xl">联系我们</h1>
          <p className="mt-4 text-sm md:text-base text-foreground-700 leading-loose max-w-2xl">
            如果你对 h动漫 有任何建议、想法，或希望与我们合作，欢迎通过下面的方式联系我们。
            我们珍惜每一条来自爱好者的声音。
          </p>
        </header>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
          <aside className="space-y-3">
            {contactInfo.map((c) => (
              <div
                key={c.label}
                className="flex items-center gap-3 rounded-lg border border-background-200/70 bg-background-50 p-4"
              >
                <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className={`${c.icon} text-lg`} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-foreground-500">{c.label}</p>
                  <p className="text-sm font-medium text-foreground-900 truncate">{c.value}</p>
                </div>
              </div>
            ))}

            <div className="rounded-lg border border-background-200/70 bg-background-50 p-4 text-center">
              <div className="w-32 h-32 mx-auto rounded-lg overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=clean%20minimal%20qr%20code%20style%20square%20graphic%20with%20warm%20orange%20accent%20frame%2C%20flat%20design%2C%20simple%20bright%20background&width=400&height=400&seq=contact-qrcode&orientation=squarish"
                  alt="h动漫 微信公众号二维码"
                  title="h动漫 微信公众号"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <p className="mt-3 text-xs text-foreground-600">扫码关注 h动漫 微信公众号</p>
            </div>
          </aside>

          <section className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-7">
            <h2 className="font-heading font-bold text-foreground-950 text-lg">发送反馈</h2>
            <p className="mt-1.5 text-sm text-foreground-600">请填写以下信息，我们会在 1-2 个工作日内回复。</p>

            {status === "success" ? (
              <div className="mt-6 rounded-lg bg-primary-50 border border-primary-200 p-6 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-500 text-background-50">
                  <i className="ri-check-line text-2xl" />
                </div>
                <h3 className="mt-4 font-heading font-semibold text-foreground-950 text-base">感谢你的反馈！</h3>
                <p className="mt-1.5 text-sm text-foreground-600">我们已收到你的留言，会尽快与你联系。</p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-4 h-9 px-4 rounded-md bg-background-50 border border-background-300 text-foreground-700 text-sm hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  再填写一条
                </button>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-5 space-y-4"
                noValidate
              >
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    姓名 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的称呼"
                    className="w-full h-11 px-3.5 rounded-md bg-background-100 border border-background-200 text-base focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-300"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    邮箱 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full h-11 px-3.5 rounded-md bg-background-100 border border-background-200 text-base focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-300"
                  />
                </div>

                <div>
                  <label htmlFor="contact-topic" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    反馈类型
                  </label>
                  <select
                    id="contact-topic"
                    name="topic"
                    defaultValue="内容建议"
                    className="w-full h-11 px-3.5 rounded-md bg-background-100 border border-background-200 text-base focus:outline-none focus:border-accent-400"
                  >
                    <option value="内容建议">内容建议</option>
                    <option value="纠错反馈">纠错反馈</option>
                    <option value="合作洽谈">合作洽谈</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    留言内容 <span className="text-primary-600">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={MESSAGE_LIMIT}
                    rows={5}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="请描述你的建议或问题……"
                    className="w-full px-3.5 py-3 rounded-md bg-background-100 border border-background-200 text-base focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-300 resize-none"
                  />
                  <p className="mt-1 text-right text-xs text-foreground-500">
                    {message.length} / {MESSAGE_LIMIT}
                  </p>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="form-hp-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && (
                  <p className="text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3 py-2">
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full sm:w-auto h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "submitting" ? "提交中…" : "提交反馈"}
                </button>
              </form>
            )}
          </section>
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-background-100 text-foreground-700 text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line" /> 返回首页
          </Link>
        </div>
      </div>
    </div>
  );
}